import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Upload, Check, AlertTriangle, X, ChevronUp } from 'lucide-react';

const SignupSummary = ({ packageData, formData, installationAddress, isSideBySideLayout = false }) => {
  // Mobile detection
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);
  const [summaryVisible, setSummaryVisible] = useState(false);
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);
  
  // Set up resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Format speed for display
  const formatSpeed = (speedKbps) => {
    const speedMbps = speedKbps / 1024;
    return speedMbps >= 1000 ? `${(speedMbps / 1000).toFixed(1)} Gbps` : `${speedMbps} Mbps`;
  };

  // Calculate costs based on package and form selections
  const costs = useMemo(() => {
    if (!packageData) return { monthly: 0, shipping: 0, installation: 0, total: 0 };

    const monthlyFee = packageData.Price || 0;
    
    // Router shipping fee if free router is selected
    const shippingFee = formData?.routerOption === 'freeRouter' ? 250 : 0;
    
    // Installation fee (could be dynamic based on other factors)
    const installationFee = 0; // Free installation as per requirements
    
    return {
      monthly: monthlyFee,
      shipping: shippingFee,
      installation: installationFee,
      total: monthlyFee + shippingFee + installationFee
    };
  }, [packageData, formData]);

  // Animation variants
  const containerVariants = isMobile
    ? {
        hidden: { opacity: 0, y: '100%' },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1],
            when: 'beforeChildren',
            staggerChildren: 0.1
          }
        },
        exit: {
          opacity: 0,
          y: '100%',
          transition: {
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1]
          }
        }
      }
    : {
        hidden: { opacity: 0, x: -20 },
        visible: {
          opacity: 1,
          x: 0,
          transition: {
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1],
            when: 'beforeChildren',
            staggerChildren: 0.1
          }
        },
        exit: {
          opacity: 0,
          x: -20,
          transition: {
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1]
          }
        }
      };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  // Mobile floating button to toggle summary
  const MobileFloatingButton = () => (
    <button
      className="fixed bottom-4 right-4 bg-purple-600 text-white rounded-full p-4 shadow-lg z-50"
      onClick={() => setSummaryVisible(true)}
    >
      <div className="relative">
        <AlertTriangle className="h-6 w-6" />
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {costs.total > costs.monthly ? '!' : ''}
        </span>
      </div>
    </button>
  );

  if (!packageData) {
    return (
      <div className={`fixed top-16 bottom-0 left-0 bg-gray-900 border-r border-gray-800 overflow-y-auto ${
        isSideBySideLayout ? 'w-1/4' : 'w-full md:w-[400px]'
      }`}>
        <div className="p-6 text-center text-gray-400">
          <AlertTriangle className="h-8 w-8 mx-auto mb-3 text-amber-400" />
          <p>No package selected</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Floating Button */}
      {isMobile && !summaryVisible && packageData && (
        <MobileFloatingButton />
      )}
      
      {/* Mobile Overlay or Desktop Sidebar */}
      <AnimatePresence>
        {(!isMobile || (isMobile && summaryVisible)) && (
          <motion.div
            className={`fixed ${
              isMobile 
                ? 'inset-0 z-50 bg-gray-900/90' 
                : `top-16 bottom-0 left-0 z-40 bg-gradient-to-b from-gray-900 to-gray-800 border-r border-gray-800 overflow-y-auto ${
                    isSideBySideLayout ? 'w-1/4' : 'w-full md:w-[400px]'
                  }`
            }`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Mobile Close Button */}
            {isMobile && (
              <button 
                className="absolute top-4 right-4 text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors z-10"
                onClick={() => setSummaryVisible(false)}
              >
                <X className="h-6 w-6" />
              </button>
            )}
            
            {/* Mobile Container */}
            {isMobile ? (
              <motion.div 
                className="fixed bottom-0 inset-x-0 bg-gradient-to-b from-gray-900 to-gray-800 rounded-t-xl max-h-[90vh] overflow-y-auto"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="p-6 pb-safe">
                  <motion.div variants={itemVariants}>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-white">Order Summary</h2>
                      <button 
                        className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800/50 transition-colors"
                        onClick={() => setSummaryVisible(false)}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <p className="text-gray-400 text-sm mb-6">Review your selected package and costs</p>
                  </motion.div>

                  {/* Package Details */}
                  <motion.div 
                    variants={itemVariants}
                    className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 mb-6"
                  >
                    <div className="p-4 border-b border-gray-700">
                      <div className="flex items-center">
                        {packageData.operator?.logo ? (
                          <img 
                            src={packageData.operator.logo} 
                            alt={packageData.operatorName} 
                            className="h-5 w-5 object-contain mr-2"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="h-5 w-5 rounded-full bg-purple-600/20 mr-2 flex items-center justify-center">
                            <span className="text-purple-400 text-xs">F</span>
                          </div>
                        )}
                        <h3 className="font-medium text-white text-sm">{packageData.operatorName}</h3>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">Uncapped Fibre</div>
                    </div>
                    
                    <div className="p-4">
                      {/* Speed */}
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <div className="text-xl font-bold text-white">{formatSpeed(packageData["Download speed (kbps)"])}</div>
                          <div className="flex items-center text-xs text-gray-400">
                            <Download className="h-3 w-3 mr-1" />
                            <span>{packageData["Download speed (kbps)"] / 1024} Mbps</span>
                            <Upload className="h-3 w-3 ml-2 mr-1" />
                            <span>{packageData["Upload speed (kbps)"] / 1024} Mbps</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-white">R{packageData.Price}</div>
                          <div className="text-xs text-gray-400">per month</div>
                        </div>
                      </div>
                      
                      {/* Features */}
                      <div className="space-y-1 mb-4">
                        <div className="flex items-center text-xs text-gray-300">
                          <Check className="h-3 w-3 text-green-400 mr-2" />
                          <span>No throttling or shaping</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-300">
                          <Check className="h-3 w-3 text-green-400 mr-2" />
                          <span>Free installation & router</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-300">
                          <Check className="h-3 w-3 text-green-400 mr-2" />
                          <span>Month-to-month contract</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Cost Breakdown */}
                  <motion.div variants={itemVariants} className="mb-6">
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Cost Breakdown</h3>
                    <div className="bg-gray-800 rounded-lg p-4">
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Monthly Fee</span>
                          <span className="text-white">R{costs.monthly.toFixed(2)}</span>
                        </div>
                        
                        {costs.shipping > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Router Shipping Fee</span>
                            <span className="text-white">R{costs.shipping.toFixed(2)}</span>
                          </div>
                        )}
                        
                        {costs.installation > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Installation Fee</span>
                            <span className="text-white">R{costs.installation.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="pt-3 border-t border-gray-700">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-300">Total Monthly Payment</span>
                          <span className="font-bold text-white">R{costs.monthly.toFixed(2)}</span>
                        </div>
                        
                        {(costs.shipping > 0 || costs.installation > 0) && (
                          <div className="flex justify-between mt-1 text-sm">
                            <span className="text-gray-400">One-time Fees</span>
                            <span className="text-white">R{(costs.shipping + costs.installation).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {/* Continue Button for Mobile */}
                  <motion.button
                    variants={itemVariants}
                    className="w-full py-3 bg-purple-600 text-white rounded-lg font-medium mt-4"
                    onClick={() => setSummaryVisible(false)}
                  >
                    Continue with Signup
                  </motion.button>
                </div>
              </motion.div>
            ) : (
              // Desktop Sidebar Content
              <div className="p-6">
                <motion.div variants={itemVariants}>
                  <h2 className="text-xl font-bold text-white mb-1">Order Summary</h2>
                  <p className="text-gray-400 text-sm mb-6">Review your selected package and costs</p>
                </motion.div>

                {/* Package Details */}
                <motion.div 
                  variants={itemVariants}
                  className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 mb-6"
                >
                  <div className="p-4 border-b border-gray-700">
                    <div className="flex items-center">
                      {packageData.operator?.logo ? (
                        <img 
                          src={packageData.operator.logo} 
                          alt={packageData.operatorName} 
                          className="h-5 w-5 object-contain mr-2"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-5 w-5 rounded-full bg-purple-600/20 mr-2 flex items-center justify-center">
                          <span className="text-purple-400 text-xs">F</span>
                        </div>
                      )}
                      <h3 className="font-medium text-white text-sm">{packageData.operatorName}</h3>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">Uncapped Fibre</div>
                  </div>
                  
                  <div className="p-4">
                    {/* Speed */}
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <div className="text-xl font-bold text-white">{formatSpeed(packageData["Download speed (kbps)"])}</div>
                        <div className="flex items-center text-xs text-gray-400">
                          <Download className="h-3 w-3 mr-1" />
                          <span>{packageData["Download speed (kbps)"] / 1024} Mbps</span>
                          <Upload className="h-3 w-3 ml-2 mr-1" />
                          <span>{packageData["Upload speed (kbps)"] / 1024} Mbps</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">R{packageData.Price}</div>
                        <div className="text-xs text-gray-400">per month</div>
                      </div>
                    </div>
                    
                    {/* Features */}
                    <div className="space-y-1 mb-4">
                      <div className="flex items-center text-xs text-gray-300">
                        <Check className="h-3 w-3 text-green-400 mr-2" />
                        <span>No throttling or shaping</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-300">
                        <Check className="h-3 w-3 text-green-400 mr-2" />
                        <span>Free installation & router</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-300">
                        <Check className="h-3 w-3 text-green-400 mr-2" />
                        <span>Month-to-month contract</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Installation Address */}
                {installationAddress && (
                  <motion.div variants={itemVariants} className="mb-6">
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Installation Address</h3>
                    <div className="bg-gray-800 rounded-lg p-3 text-sm text-gray-300">
                      <p>{formData?.streetName || installationAddress.mainText || ''}</p>
                      {formData?.complex && (
                        <p>
                          {formData.complex}
                          {formData?.unitNumber ? `, Unit ${formData.unitNumber}` : ''}
                        </p>
                      )}
                      <p>
                        {formData?.suburb || ''}
                        {formData?.city ? `, ${formData.city}` : ''}
                        {formData?.postalCode ? `, ${formData.postalCode}` : ''}
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Cost Breakdown */}
                <motion.div variants={itemVariants} className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-2">Cost Breakdown</h3>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Monthly Fee</span>
                        <span className="text-white">R{costs.monthly.toFixed(2)}</span>
                      </div>
                      
                      {costs.shipping > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Router Shipping Fee</span>
                          <span className="text-white">R{costs.shipping.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {costs.installation > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Installation Fee</span>
                          <span className="text-white">R{costs.installation.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="pt-3 border-t border-gray-700">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-300">Total Monthly Payment</span>
                        <span className="font-bold text-white">R{costs.monthly.toFixed(2)}</span>
                      </div>
                      
                      {(costs.shipping > 0 || costs.installation > 0) && (
                        <div className="flex justify-between mt-1 text-sm">
                          <span className="text-gray-400">One-time Fees</span>
                          <span className="text-white">R{(costs.shipping + costs.installation).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Router Selection Summary */}
                {formData?.routerOption && (
                  <motion.div variants={itemVariants} className="mb-6">
                    <h3 className="text-sm font-medium text-gray-300 mb-2">Router Selection</h3>
                    <div className="bg-gray-800 rounded-lg p-3 text-sm">
                      {formData.routerOption === 'freeRouter' ? (
                        <div className="flex items-start">
                          <Check className="h-4 w-4 text-green-400 mr-2 mt-0.5" />
                          <div>
                            <p className="text-white">Free to Use Router</p>
                            <p className="text-xs text-amber-400">R250 Shipping Fee applies</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <Check className="h-4 w-4 text-green-400 mr-2" />
                          <p className="text-white">Using Own Router</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Help Text */}
                <motion.div variants={itemVariants} className="text-xs text-gray-400 mt-8">
                  <p>Need help? Contact our support team at support@example.com or call 0800 123 456.</p>
                </motion.div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SignupSummary;